/**
 * Runs one engine script and returns its body, or throws an
 * `EngineScriptError` with the engine's own message. The engine answers 200
 * even when a script fails (the failure is in `Err code` / `Err msg` or a
 * last log line other than the success line), and 202 with an async id for
 * long runs, which is polled until ready and then fetched.
 */

import { EngineQueryResult } from "types/engineOutput.types"

export const SUCCESS_LOG = "Script successfully executed."

export class EngineScriptError extends Error {
  constructor(
    message: string,
    public readonly log: string[]
  ) {
    super(message)
    this.name = "EngineScriptError"
  }
}

/** The engine's error message for `body`, or `null` when the run succeeded. */
export const extractEngineError = (body: unknown): string | null => {
  if (typeof body !== "object" || body === null) return "Empty engine response"
  const rec = body as Record<string, unknown>
  const code =
    typeof rec["Err code"] === "number" ? (rec["Err code"] as number) : 0
  const msg = typeof rec["Err msg"] === "string" ? (rec["Err msg"] as string) : null
  const log = Array.isArray((rec.result as { log?: unknown } | undefined)?.log)
    ? (rec.result as { log: unknown[] }).log.filter(
        (l): l is string => typeof l === "string"
      )
    : []
  const last = log[log.length - 1] ?? ""
  if (code !== 0) return msg || last || `Engine error ${code}`
  if (msg) return msg
  if (log.length > 0 && last !== SUCCESS_LOG) return last
  return null
}

export type RunScript = (script: string) => Promise<EngineQueryResult>

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((r) => setTimeout(r, ms))

export const createEngineRunner = (deps: {
  run: (script: string) => Promise<{ status: number; data: unknown }>
  poll: (id: string) => Promise<{ status: number }>
  fetch: (id: string) => Promise<{ data: unknown }>
  pollIntervalMs?: number
  /** Longest total wait for a 202 job, counted in slept ms. */
  maxWaitMs?: number
  sleep?: (ms: number) => Promise<void>
}): RunScript => {
  const interval = deps.pollIntervalMs ?? 2000
  const maxWait = deps.maxWaitMs ?? 120000
  const sleep = deps.sleep ?? defaultSleep
  return async (script) => {
    const first = await deps.run(script)
    let body = first.data
    if (first.status === 202) {
      const id = String(first.data).trim()
      // The queue is serial, so a job that never finishes must not block
      // every tile behind it.
      let elapsed = 0
      for (;;) {
        const p = await deps.poll(id)
        if (p.status === 200) break
        await sleep(interval)
        elapsed += interval
        if (elapsed > maxWait) {
          throw new EngineScriptError(
            `The query timed out after ${Math.round(maxWait / 1000)} seconds.`,
            []
          )
        }
      }
      body = (await deps.fetch(id)).data
    }
    const error = extractEngineError(body)
    if (error) {
      const log = ((body as { result?: { log?: string[] } } | null)?.result
        ?.log ?? []) as string[]
      throw new EngineScriptError(error, log)
    }
    return body as EngineQueryResult
  }
}
